// Richtprijzen per dienst — één plek om bedragen aan te passen.
// `prijs: null` toont "Prijs op aanvraag" op de kostenpagina.
export interface KostenItem {
  /** Slug van de dienst (matcht src/lib/data.ts). */
  slug: string;
  title: string;
  /** Richtprijs-indicatie, of null als die nog ontbreekt. */
  prijs: string | null;
  /** Korte toelichting bij de richtprijs (waar de bandbreedte over gaat). */
  prijsNoot?: string;
  /** Waar de prijs vooral van afhangt. */
  factoren: string[];
}

export const kostenItems: KostenItem[] = [
  {
    slug: "dakkapel",
    title: "Dakkapel",
    prijs: "± € 7.500 – € 15.000",
    prijsNoot:
      "Voor een dakkapel van circa 2 tot 4 meter breed, inclusief plaatsing en afwerking.",
    factoren: [
      "Breedte van de dakkapel en aantal ramen",
      "Materiaal en afwerking (hout, kunststof, aluminium)",
      "Isolatiewaarde en type glas",
      "Bereikbaarheid van het dak (steiger of hoogwerker)",
    ],
  },
  {
    slug: "dakkapel-renovatie",
    title: "Dakkapel renovatie",
    prijs: "± € 2.500 – € 7.500",
    prijsNoot:
      "Van nieuwe bekleding en kozijnen tot volledig strippen en isoleren.",
    factoren: [
      "Staat van de bestaande constructie",
      "Wel of niet meteen isoleren",
      "Nieuwe bekleding en/of nieuwe kozijnen",
      "Of het dak in dezelfde beurt wordt meegenomen",
    ],
  },
  {
    slug: "aanbouw",
    title: "Aanbouw of uitbouw",
    prijs: "± € 1.800 – € 2.600 per m²",
    prijsNoot:
      "Houtskeletbouw aan de onderkant, metselwerk met hoogwaardige afwerking aan de bovenkant.",
    factoren: [
      "Oppervlakte en bouwmethode (prefab, metselwerk, serre)",
      "Fundering — in de regio vaak op palen",
      "Doorbreken van een draagmuur (constructieberekening)",
      "Afwerkingsniveau en installaties",
    ],
  },
  {
    slug: "dakopbouw",
    title: "Dakopbouw",
    prijs: "± € 25.000 – € 60.000",
    prijsNoot:
      "Een volledige extra verdieping, inclusief constructie, isolatie en afwerking.",
    factoren: [
      "Oppervlakte van de extra verdieping",
      "Constructieve aanpassingen en vergunning",
      "Trap, installaties en afwerking",
      "Isolatie en dakbedekking",
    ],
  },
  {
    slug: "kozijnen",
    title: "Kozijnen",
    prijs: "± € 900 – € 1.800 per kozijn",
    prijsNoot:
      "Inclusief HR++ glas en plaatsing; grote of samengestelde kozijnen liggen hoger.",
    factoren: [
      "Aantal kozijnen en maatvoering",
      "Hout of kunststof",
      "Glaskeuze (HR++ of triple)",
      "Draai-kiepdelen, roosters en hor",
    ],
  },
  {
    slug: "vlonder",
    title: "Vlonder",
    prijs: "± € 90 – € 250 per m²",
    prijsNoot:
      "Vuren aan de onderkant van de bandbreedte, hardhout en composiet aan de bovenkant.",
    factoren: [
      "Oppervlakte van het terras",
      "Houtsoort (vuren, hardhout, composiet)",
      "Onderconstructie en grondwerk",
      "Hoogteverschil en trapjes",
    ],
  },
  {
    slug: "schuur",
    title: "Schuur & overkapping",
    prijs: "± € 3.000 – € 9.000",
    prijsNoot:
      "Afhankelijk van afmeting, houtsoort, isolatie, dakbedekking en type (schuur of overkapping), inclusief fundering.",
    factoren: [
      "Afmetingen en indeling",
      "Houtsoort en dakbedekking",
      "Fundering of betonvloer",
      "Isolatie, ramen, deuren en elektra",
    ],
  },
  {
    slug: "renovatie",
    title: "Renovatie",
    prijs: "± € 400 – € 900 per m²",
    prijsNoot:
      "Van één ruimte opknappen tot een volledige verbouwing met nieuwe indeling.",
    factoren: [
      "Omvang: één ruimte of de hele woning",
      "Sloopwerk en afvoer",
      "Nieuwe indeling en constructieve ingrepen",
      "Verduurzaming — mogelijk met subsidie",
    ],
  },
  {
    slug: "specials",
    title: "Specials en maatwerk",
    prijs: "± € 55 per uur + materiaal",
    prijsNoot:
      "Maatwerk rekenen we op basis van uren; na de opname krijg je een vaste prijs.",
    factoren: [
      "Complexiteit van het ontwerp",
      "Materiaalkeuze",
      "Benodigde uren en voorbereiding",
    ],
  },
];

/** Uitleg onder de prijskaarten — één plek om het prijspeil bij te werken. */
export const kostenDisclaimer =
  "Alle bedragen zijn richtprijzen inclusief montage en btw, prijspeil 2026. De uiteindelijke prijs volgt uit een gratis opname bij je thuis.";

export const kostenFaq = [
  {
    vraag: "Wat kost een adviesgesprek of offerte?",
    antwoord:
      "Niets. We komen vrijblijvend bij je langs, bekijken de situatie ter plaatse en maken daarna een offerte op maat. Vaak kunnen we tijdens dat gesprek al een eerste inschatting van de kosten geven.",
  },
  {
    vraag: "Waarom staan er richtprijzen en geen vaste prijzen?",
    antwoord:
      "De richtprijzen op deze pagina geven een goed beeld, maar we maken alles ter plekke op maat. Afmetingen, materiaalkeuze, bereikbaarheid en de staat van de bestaande constructie bepalen samen de definitieve prijs, dus de offerte is altijd toegespitst op jouw situatie.",
  },
  {
    vraag: "Kan ik subsidie krijgen op de werkzaamheden?",
    antwoord:
      "Bij verduurzaming — zoals nieuwe kozijnen met isolerend glas of extra isolatie — kom je vaak in aanmerking voor subsidie. Onze offertes en facturen zijn geschikt voor je subsidieaanvraag.",
  },
  {
    vraag: "Hoe wordt er betaald?",
    antwoord:
      "In termijnen die we vooraf in de offerte vastleggen: een aanbetaling bij de start, eventueel een termijn tijdens de uitvoering en het restant na oplevering.",
  },
];
